import { Component, DestroyRef, ElementRef, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Devices } from '@app/_models/devices';
import { Property } from '@app/_models/propery.model';
import { AccessManagementService, GuestDeviceDetails, GuestDeviceOperation } from '@app/_services/access-management.service';
import { PropertyService } from '@app/_services/property.service';
import { faArrowRight, faSpinner, faUnlock, faWalking, faCar, faMicrochip, faLightbulb, faFan, faDroplet, faPlug, faPowerOff, faClock, faRotateRight, faXmark, faPlay } from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, timeout } from 'rxjs/operators';
import { throwError } from 'rxjs';

interface GuestProfile {
  guestid?: string | number | null;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
  stopdtime?: string | null;
}

interface GuestLoginResponse extends GuestProfile {
  username?: string;
  access_token?: string;
  devices?: any;
  message?: string;
}

interface GuestDeviceControl {
  details?: GuestDeviceDetails;
  modes: string[];
  loading: boolean;
  pending: boolean;
  isOn: boolean;
  error: string;
}

@Component({
  selector: 'app-guest-access',
  templateUrl: './guest-access.component.html',
  styleUrls: ['./guest-access.component.css'],
  standalone: false
})
export class GuestAccessComponent implements OnInit {
  faArrowRight = faArrowRight;
  faSpinner = faSpinner;
  faUnlock = faUnlock;
  faWalking = faWalking;
  faCar = faCar;
  faMicrochip = faMicrochip;
  faPowerOff = faPowerOff;
  faClock = faClock;
  faRotateRight = faRotateRight;
  faXmark = faXmark;
  faPlay = faPlay;

  @ViewChild('deviceDelayDialog') deviceDelayDialog?: ElementRef<HTMLDialogElement>;

  guestForm!: FormGroup;
  newGuestForm!: FormGroup;
  delayForm!: FormGroup;
  delayDevice = signal<Devices | null>(null);
  deviceControls = signal<Record<number, GuestDeviceControl>>({});
  isRefreshingDevices = computed(() => Object.values(this.deviceControls()).some(control => control.loading));
  hasPendingDevice = computed(() => Object.values(this.deviceControls()).some(control => control.pending));

  isLoadingSavedCredentials = signal(true);
  isSubmitting = signal(false);
  isSubmittingRequest = signal(false);
  guestAuthenticated = signal(false);
  guestUsername = signal('');
  guestAccessToken = signal('');
  guestProfile = signal<GuestProfile>({});
  expirationDate = computed(() => {
    const raw = this.guestProfile().stopdtime?.trim() || '';
    if (!raw) {
      return '-';
    }
    const normalized = raw.replace(' ', 'T');
    const date = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized) ? normalized : `${normalized}Z`);
    return Number.isNaN(date.getTime()) ? raw : formatDate(date, 'yyyy-MM-dd HH:mm', 'en-US');
  });
  devices = signal<Devices[]>([]);
  selectedDeviceTab = signal<'gate' | 'device' | 'profile'>('gate');
  smartDevices = computed(() => this.devices().filter(device => device.type === 'device'));
  deviceSwitchStates = signal<Record<number, boolean>>({});
  requestType = signal<'existing' | 'new'>('existing');
  properties = signal<Property[]>([]);
  isLoadingProperties = signal(false);

  private destroyRef = inject(DestroyRef);
  private deviceTimers = new Map<number, ReturnType<typeof setTimeout>>();

  constructor(
    private formBuilder: FormBuilder,
    private accessManagementService: AccessManagementService,
    private propertyService: PropertyService,
    private toastr: ToastrService
  ) {
    this.destroyRef.onDestroy(() => this.clearDeviceTimers());
  }

  ngOnInit(): void {
    this.delayForm = this.formBuilder.group({
      minutes: [5, [Validators.required, (control: AbstractControl) => {
        const minutes = Number(control.value);
        return Number.isFinite(minutes) && minutes > 0 && minutes <= 1440 && Math.round(minutes * 60) >= 1
          ? null : { duration: true };
      }]]
    });

    this.guestForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      accessKey: ['', Validators.required]
    });

    this.newGuestForm = this.formBuilder.group({
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      phone: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      propertyId: [null, Validators.required],
      accesskey: [''],
      index: [''],
      unitnumber: ['']
    });

    const savedEmail = localStorage.getItem('guest_email') || '';
    const savedAccessKey = localStorage.getItem('guest_access_key') || '';

    if (savedEmail) {
      this.guestForm.patchValue({ email: savedEmail });
    }

    if (savedAccessKey) {
      this.guestForm.patchValue({ accessKey: savedAccessKey });
    }

    this.isLoadingSavedCredentials.set(false);
    this.loadProperties();
  }

  get f() {
    return this.guestForm.controls;
  }

  continueGuestAccess(): void {
    if (this.guestForm.invalid) {
      this.toastr.info('Enter a valid email and access key.', 'Information');
      this.guestForm.markAllAsTouched();
      return;
    }

    const email = String(this.guestForm.value.email || '').trim().toLowerCase();
    const accesskey = String(this.guestForm.value.accessKey || '').trim();

    this.isSubmitting.set(true);

    this.accessManagementService.guestLogin(email, accesskey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: GuestLoginResponse) => {
          const guestUsername = String(response?.username || '').trim();
          const guestToken = String(response?.access_token || '').trim();

          if (!guestUsername || !guestToken) {
            this.isSubmitting.set(false);
            this.toastr.error('Guest login succeeded but required session data is missing.', 'Login Failed');
            return;
          }

          localStorage.setItem('guest_email', email);
          localStorage.setItem('guest_access_key', accesskey);
          localStorage.setItem('guest_username', guestUsername);
          localStorage.setItem('guest_access_token', guestToken);

          this.guestUsername.set(guestUsername);
          this.guestAccessToken.set(guestToken);
          this.guestProfile.set({ guestid: guestUsername, email });
          this.updateGuestProfile(response);
          this.guestAuthenticated.set(true);

          const loginDevices = this.extractGuestDevices(response?.devices);
          this.loadSubscribedDevices(email, guestToken, loginDevices);
        },
        error: (error) => {
          this.isSubmitting.set(false);
          const message = String(error?.error?.message || 'Unable to verify your guest access right now. Please try again.');

          if (message.toLowerCase().includes('guest access expired')) {
            this.clearGuestSession();
            this.guestForm.patchValue({ accessKey: '' });
            this.toastr.error(message, 'Guest Access Expired');
            return;
          }

          this.toastr.error(message, 'Access Not Approved');
        }
      });
  }

  selectRequestType(type: 'existing' | 'new'): void {
    this.requestType.set(type);
  }

  isHomePropertySelected(): boolean {
    return Number(this.newGuestForm.value.propertyId) === 1000;
  }

  submitNewGuestRequest(): void {
    this.newGuestForm.markAllAsTouched();

    if (this.newGuestForm.invalid) {
      this.toastr.info('Please complete all required fields.', 'Information');
      return;
    }

    const homeProperty = this.isHomePropertySelected();
    const propertyId = Number(this.newGuestForm.value.propertyId);
    const email = String(this.newGuestForm.value.email || '').trim().toLowerCase();
    const accesskey = String(this.newGuestForm.value.accesskey || '').trim();
    const indexText = String(this.newGuestForm.value.index || '').trim();
    const unitText = String(this.newGuestForm.value.unitnumber || '').trim();

    if (homeProperty && !accesskey) {
      this.toastr.error('Access Key is required for this property.', 'Invalid Details');
      return;
    }

    if (homeProperty && !indexText) {
      this.toastr.error('Index is required for this property.', 'Invalid Details');
      return;
    }

    if (!homeProperty && !unitText) {
      this.toastr.error('Unit/Lot Number is required.', 'Invalid Details');
      return;
    }

    const payload = {
      firstname: String(this.newGuestForm.value.firstname || '').trim(),
      lastname: String(this.newGuestForm.value.lastname || '').trim(),
      phone: String(this.newGuestForm.value.phone || '').trim(),
      email,
      property_id: propertyId,
      accesskey: homeProperty ? accesskey : '',
      unitnumber: Number(unitText || '0') || 0,
      index: Number(indexText || '0') || 0,
      createdby: email
    };

    this.isSubmittingRequest.set(true);
    this.accessManagementService.createGuestAccessRequest(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.toastr.success(response?.message || 'Temporary access request submitted.', 'Request Submitted');
          this.guestForm.patchValue({ email });
          this.selectRequestType('existing');
          this.newGuestForm.patchValue({
            firstname: '',
            lastname: '',
            phone: '',
            email,
            propertyId: null,
            accesskey: '',
            index: '',
            unitnumber: ''
          });
          this.newGuestForm.markAsPristine();
          this.newGuestForm.markAsUntouched();
          this.isSubmittingRequest.set(false);
        },
        error: (error) => {
          const message = this.extractErrorMessage(error) || 'Unable to submit your request right now. Please try again.';
          this.toastr.error(message, 'Request Failed');
          this.isSubmittingRequest.set(false);
        }
      });
  }

  toggleGate(device: Devices, checked: boolean): void {
    const deviceId = Number(device.id);
    if (!Number.isFinite(deviceId)) {
      return;
    }

    const states = { ...this.deviceSwitchStates() };
    states[deviceId] = checked;
    this.deviceSwitchStates.set(states);

    if (!checked) {
      return;
    }

    const label = String(device.labeltxt || '').toLowerCase();
    const relayIndex = (label.includes('pedestrian') || label.includes('ped')) ? 0 : 1;
    const guestToken = this.guestAccessToken() || localStorage.getItem('guest_access_token') || '';

    const requestPublicIp = {
      accessby: this.guestUsername() || 'guest',
      devicename: String(device.name || ''),
      labeltxt: String(device.labeltxt || ''),
      relayindex: relayIndex,
      accessmethod: 'public_ip'
    };

    const requestLocalIp = {
      ...requestPublicIp,
      accessmethod: 'local_ip'
    };

    this.accessManagementService.operateGuestDevice(requestPublicIp, guestToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(
        catchError(() => {
          return this.accessManagementService.operateGuestDevice(requestLocalIp, guestToken);
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (resp) => {
          this.toastr.success(resp?.message || 'Instruction sent to gate.', 'Success');
        },
        error: () => {
          this.toastr.error('Unable to send gate command.', 'Error');
        }
      });

    setTimeout(() => {
      const current = { ...this.deviceSwitchStates() };
      current[deviceId] = false;
      this.deviceSwitchStates.set(current);
    }, 8000);
  }

  isPedestrianGate(device: Devices): boolean {
    const label = String(device.labeltxt || '').toLowerCase();
    return label.includes('pedestrian') || label.includes('ped');
  }

  sortedDevices(): Devices[] {
    return this.devices().filter(device => device.type === 'gate').sort((a, b) => {
      const aPriority = this.deviceSortPriority(String(a.labeltxt || ''));
      const bPriority = this.deviceSortPriority(String(b.labeltxt || ''));
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      const aLabel = String(a.labeltxt || '').toLowerCase();
      const bLabel = String(b.labeltxt || '').toLowerCase();
      return aLabel.localeCompare(bLabel);
    });
  }

  deviceLabel(device: Devices): string {
    const details = this.deviceControls()[Number(device.id)]?.details;
    return details?.label?.trim() || details?.labeltxt?.trim() || device.labeltxt || device.name || `Device ${device.id}`;
  }

  deviceIcon(device: Devices) {
    switch (this.deviceControls()[Number(device.id)]?.details?.device_type) {
      case 'light': return faLightbulb;
      case 'fan': return faFan;
      case 'pump': return faDroplet;
      case 'plug': return faPlug;
      default: return faMicrochip;
    }
  }

  loadDeviceDetails(deviceId?: number): void {
    const token = this.guestAccessToken().trim();
    for (const device of this.smartDevices()) {
      const id = Number(device.id);
      if (!Number.isInteger(id) || id <= 0 || (deviceId !== undefined && id !== deviceId)) {
        continue;
      }
      const previous = this.deviceControls()[id];
      if (previous?.loading || previous?.pending) {
        continue;
      }
      this.updateDeviceControl(id, {
        ...(previous ?? { modes: [], loading: false, pending: false, isOn: false, error: '' }),
        error: token ? '' : 'Guest session expired. Please sign in again.'
      });
      if (!token) {
        continue;
      }
      this.updateDeviceControl(id, { loading: true });
      this.accessManagementService.getGuestDeviceDetails(id, token)
        .pipe(timeout(30000), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: details => {
            if (!details || typeof details !== 'object' || Array.isArray(details)) {
              this.updateDeviceControl(id, { loading: false, error: 'Invalid device details.' });
              return;
            }
            const modes = Array.isArray(details.operation_modes)
              ? details.operation_modes.filter(mode => typeof mode === 'string').map(mode => mode.toLowerCase()) : [];
            this.updateDeviceControl(id, { details, modes, loading: false });
          },
          error: error => this.updateDeviceControl(id, {
            loading: false, error: this.extractErrorMessage(error) || 'Unable to load device details. Please try again.'
          })
        });
    }
  }

  operateSmartDevice(device: Devices, operation: GuestDeviceOperation, nextState: boolean, duration = 5): void {
    const id = Number(device.id);
    const control = this.deviceControls()[id];
    if (!control?.details || control.loading || control.pending || !control.modes.includes(operation.toLowerCase())) {
      return;
    }
    if (operation !== 'ON/OFF' && (!Number.isInteger(duration) || duration < 1 || duration > 86400)) {
      return;
    }
    const token = this.guestAccessToken().trim();
    if (!token) {
      this.updateDeviceControl(id, { error: 'Guest session expired. Please sign in again.' });
      return;
    }
    this.updateDeviceControl(id, { pending: true, error: '' });
    this.accessManagementService.operateGuestSmartDevice(id, operation, nextState, operation === 'ON/OFF' ? 0 : duration, token)
      .pipe(timeout(30000), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.updateDeviceControl(id, { pending: false, isOn: nextState });
          clearTimeout(this.deviceTimers.get(id));
          this.deviceTimers.delete(id);
          if (nextState && operation !== 'ON/OFF') {
            this.deviceTimers.set(id, setTimeout(() => {
              this.deviceTimers.delete(id);
              this.updateDeviceControl(id, { isOn: false });
            }, duration * 1000));
          }
          this.toastr.success(response?.message || 'Device instruction sent.', 'Success');
        },
        error: error => this.updateDeviceControl(id, {
          pending: false, error: this.extractErrorMessage(error) || 'Unable to operate device. Please try again.'
        })
      });
  }

  toggleSmartDevice(device: Devices, input: HTMLInputElement): void {
    const isOn = this.deviceControls()[Number(device.id)]?.isOn ?? false;
    input.checked = isOn;
    this.operateSmartDevice(device, 'TOGGLE', !isOn);
  }

  openDelayDialog(device: Devices): void {
    const control = this.deviceControls()[Number(device.id)];
    if (!control?.details || control.loading || control.pending || !control.modes.includes('delay')) {
      return;
    }
    this.delayDevice.set(device);
    this.delayForm.reset({ minutes: 5 });
    this.deviceDelayDialog?.nativeElement.showModal();
  }

  closeDelayDialog(): void {
    this.deviceDelayDialog?.nativeElement.close();
    this.delayDevice.set(null);
  }

  submitDeviceDelay(): void {
    this.delayForm.markAllAsTouched();
    const device = this.delayDevice();
    if (this.delayForm.invalid || !device) {
      return;
    }
    this.operateSmartDevice(device, 'DELAY', true, Math.round(Number(this.delayForm.value.minutes) * 60));
    this.closeDelayDialog();
  }

  private updateDeviceControl(id: number, changes: Partial<GuestDeviceControl>): void {
    this.deviceControls.update(controls => ({ ...controls, [id]: { ...controls[id], ...changes } }));
  }

  private clearDeviceTimers(): void {
    this.deviceTimers.forEach(timer => clearTimeout(timer));
    this.deviceTimers.clear();
  }

  private updateGuestProfile(profile: GuestProfile): void {
    this.guestProfile.update(current => ({
      guestid: profile.guestid ?? current.guestid,
      firstname: profile.firstname ?? current.firstname,
      lastname: profile.lastname ?? current.lastname,
      email: profile.email ?? current.email,
      stopdtime: profile.stopdtime ?? current.stopdtime
    }));
  }

  private loadSubscribedDevices(email: string, token: string, fallbackDevices: Devices[]): void {
    this.accessManagementService.getGuestSubscriptions(email, token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.updateGuestProfile(response || {});
          const resolved = this.extractGuestDevices(response?.devices);
          const devices = resolved.length ? resolved : fallbackDevices;
          this.devices.set(devices);
          this.initializeSwitchStates(devices);
          this.isSubmitting.set(false);
        },
        error: () => {
          this.devices.set(fallbackDevices);
          this.initializeSwitchStates(fallbackDevices);
          this.isSubmitting.set(false);
        }
      });
  }

  private loadProperties(): void {
    this.isLoadingProperties.set(true);

    this.propertyService.getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const properties = (response || []).filter((property) => property.enabled !== false);
          this.properties.set(properties);
          this.isLoadingProperties.set(false);
        },
        error: () => {
          this.properties.set([]);
          this.isLoadingProperties.set(false);
          this.toastr.error('Unable to load properties right now. Please try again later.', 'Error');
        }
      });
  }

  private initializeSwitchStates(devices: Devices[]): void {
    const states: Record<number, boolean> = {};
    devices.forEach((device) => {
      const id = Number(device.id);
      if (Number.isFinite(id)) {
        states[id] = false;
      }
    });

    this.deviceSwitchStates.set(states);
    this.clearDeviceTimers();
    this.deviceControls.set({});
    this.loadDeviceDetails();
  }

  private extractGuestDevices(devicesValue: any): Devices[] {
    let values: any[] = [];

    if (Array.isArray(devicesValue)) {
      values = devicesValue;
    } else if (typeof devicesValue === 'string' && devicesValue.trim()) {
      try {
        const parsed = JSON.parse(devicesValue);
        if (Array.isArray(parsed)) {
          values = parsed;
        }
      } catch {
        values = [];
      }
    }

    return values.map((entry: any, idx: number) => ({
      id: Number(entry?.id || idx + 1),
      type: String(entry?.type || ''),
      name: String(entry?.name || ''),
      labeltxt: String(entry?.label || entry?.labeltxt || entry?.labelTxt || ''),
      description: String(entry?.description || ''),
      relayindex: Number(entry?.relayindex || 0),
      enabled: true,
      property_id: Number(entry?.property_id || 0)
    } as Devices));
  }

  private deviceSortPriority(labelText: string): number {
    const label = labelText.toLowerCase();
    if (label.includes('pedestrian') || label.includes('ped')) {
      return 0;
    }

    if (label.includes('vehicle') || label.includes('veh')) {
      return 1;
    }

    return 2;
  }

  private clearGuestSession(): void {
    localStorage.removeItem('guest_email');
    localStorage.removeItem('guest_access_key');
    localStorage.removeItem('guest_username');
    localStorage.removeItem('guest_access_token');
    this.guestAuthenticated.set(false);
    this.guestUsername.set('');
    this.guestAccessToken.set('');
    this.guestProfile.set({});
    this.devices.set([]);
    this.selectedDeviceTab.set('gate');
    this.deviceSwitchStates.set({});
    this.clearDeviceTimers();
    this.deviceControls.set({});
    this.closeDelayDialog();
  }

  private extractErrorMessage(error: any): string {
    if (!error) {
      return '';
    }

    if (typeof error === 'string') {
      return error;
    }

    const candidate = error?.error?.message || error?.message || error?.statusText;
    return typeof candidate === 'string' ? candidate : '';
  }
}
